(() => {
  const GROUP_SELECTORS = [
    '.wubs-agency-about__departments',
    '.wubs-agency-services-accordion__list',
    '.wubs-agency-migration-faq__list',
    '.wubs-service-detail__accordion',
    '.wubs-city-page__process-list',
    '.wubs-city-page__faq-list'
  ];

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ensureStyle = () => {
    if (document.getElementById('wubs-accordion-runtime-style')) return;

    const style = document.createElement('style');
    style.id = 'wubs-accordion-runtime-style';
    style.textContent = `
      details.is-wubs-accordion {
        overflow-anchor: none;
      }

      details.is-wubs-accordion::details-content {
        block-size: auto !important;
        opacity: 1 !important;
        overflow: visible !important;
        transition: none !important;
      }

      details.is-wubs-accordion[open]::details-content,
      details.is-wubs-accordion.is-wubs-opening::details-content,
      details.is-wubs-accordion.is-wubs-closing::details-content {
        block-size: auto !important;
        opacity: 1 !important;
        overflow: visible !important;
        transition: none !important;
      }

      details.is-wubs-accordion > summary {
        -webkit-tap-highlight-color: transparent;
      }

      details.is-wubs-accordion .is-wubs-accordion-panel {
        will-change: height, opacity, transform;
      }

      details.is-wubs-accordion.is-wubs-opening > :not(summary):not(.is-wubs-accordion-panel),
      details.is-wubs-accordion.is-wubs-closing > :not(summary):not(.is-wubs-accordion-panel) {
        animation: none !important;
        transition: none !important;
      }
    `;
    document.head.appendChild(style);
  };

  const getPanel = (details) => {
    const summary = details.querySelector(':scope > summary');
    return summary ? summary.nextElementSibling : null;
  };

  const cleanupPanel = (details, panel) => {
    if (!panel) return;
    panel.style.height = '';
    panel.style.paddingTop = '';
    panel.style.paddingBottom = '';
    panel.style.boxSizing = '';
    panel.style.opacity = '';
    panel.style.overflow = '';
    panel.style.transform = '';
    panel.style.transition = '';
    details.classList.remove('is-wubs-opening', 'is-wubs-closing');
  };

  const clearAnimation = (details) => {
    if (!details._wubsAccordionAnimation) return;
    const animation = details._wubsAccordionAnimation;
    if (animation.frame) cancelAnimationFrame(animation.frame);
    if (animation.timer) clearTimeout(animation.timer);
    if (animation.panel && animation.onTransitionEnd) {
      animation.panel.removeEventListener('transitionend', animation.onTransitionEnd);
    }
    details._wubsAccordionAnimation = null;
  };

  const captureCurrentState = (details, panel) => {
    if (!panel) return { height: 0, paddingTop: '0px', paddingBottom: '0px' };

    const computed = window.getComputedStyle(panel);
    const state = {
      height: Math.max(0, panel.getBoundingClientRect().height),
      paddingTop: computed.paddingTop,
      paddingBottom: computed.paddingBottom
    };

    if (details._wubsAccordionAnimation) {
      panel.style.transition = 'none';
      panel.style.boxSizing = 'border-box';
      panel.style.height = `${state.height}px`;
      panel.style.paddingTop = state.paddingTop;
      panel.style.paddingBottom = state.paddingBottom;
      clearAnimation(details);
    }

    return state;
  };

  const measureExpandedState = (panel) => {
    const properties = ['height', 'paddingTop', 'paddingBottom', 'boxSizing', 'opacity', 'overflow', 'transform', 'transition'];
    const previous = {};

    properties.forEach((property) => {
      previous[property] = panel.style[property];
    });

    panel.style.transition = 'none';
    panel.style.boxSizing = 'border-box';
    panel.style.height = 'auto';
    panel.style.paddingTop = '';
    panel.style.paddingBottom = '';
    panel.style.opacity = '1';
    panel.style.overflow = 'hidden';
    panel.style.transform = 'translateY(0)';

    const computed = window.getComputedStyle(panel);
    const expanded = {
      height: Math.max(0, panel.getBoundingClientRect().height),
      paddingTop: computed.paddingTop,
      paddingBottom: computed.paddingBottom
    };

    properties.forEach((property) => {
      panel.style[property] = previous[property];
    });

    return expanded;
  };

  const animatePanel = (details, panel, targetStyles, options, onDone) => {
    clearAnimation(details);

    return new Promise((resolve) => {
      const finish = () => {
        if (!details._wubsAccordionAnimation || details._wubsAccordionAnimation.finish !== finish) return;

        panel.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(details._wubsAccordionAnimation.timer);
        details._wubsAccordionAnimation = null;
        panel.style.transition = '';
        onDone();
        resolve();
      };

      const onTransitionEnd = (event) => {
        if (event.target === panel && event.propertyName === 'height') finish();
      };

      details._wubsAccordionAnimation = {
        panel,
        frame: requestAnimationFrame(() => {
          panel.style.transition = [
            `height ${options.duration}ms ${options.easing}`,
            `padding-top ${options.duration}ms ${options.easing}`,
            `padding-bottom ${options.duration}ms ${options.easing}`,
            `opacity ${Math.max(180, options.duration - 80)}ms ${options.easing}`,
            `transform ${options.duration}ms ${options.easing}`
          ].join(', ');

          panel.addEventListener('transitionend', onTransitionEnd);
          requestAnimationFrame(() => {
            Object.entries(targetStyles).forEach(([property, value]) => {
              panel.style[property] = value;
            });
          });
        }),
        timer: setTimeout(finish, options.duration + 120),
        onTransitionEnd,
        finish
      };
    });
  };

  const keepSummaryStable = (summary, promise, duration = 460) => {
    if (!summary || prefersReducedMotion()) return promise;

    const targetTop = summary.getBoundingClientRect().top;
    const startedAt = performance.now();
    let active = true;

    const tick = () => {
      if (!active) return;

      const currentTop = summary.getBoundingClientRect().top;
      const delta = currentTop - targetTop;
      if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);

      if (performance.now() - startedAt < duration) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);

    return promise.finally(() => {
      setTimeout(() => {
        active = false;
      }, 80);
    });
  };

  const closeDetails = (details, instant = false) => {
    const panel = getPanel(details);

    if (!details.open || !panel) {
      details.open = false;
      cleanupPanel(details, panel);
      return Promise.resolve();
    }

    const current = captureCurrentState(details, panel);
    const startHeight = current.height || panel.getBoundingClientRect().height;

    if (instant || prefersReducedMotion() || startHeight <= 1) {
      details.open = false;
      cleanupPanel(details, panel);
      return Promise.resolve();
    }

    details.classList.remove('is-wubs-opening');
    details.classList.add('is-wubs-closing');
    panel.style.boxSizing = 'border-box';
    panel.style.height = `${startHeight}px`;
    panel.style.paddingTop = current.paddingTop;
    panel.style.paddingBottom = current.paddingBottom;
    panel.style.opacity = '1';
    panel.style.overflow = 'hidden';
    panel.style.transform = 'translateY(0)';
    panel.getBoundingClientRect();

    return animatePanel(
      details,
      panel,
      { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: '0', transform: 'translateY(-6px)' },
      { duration: 280, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
      () => {
        details.open = false;
        cleanupPanel(details, panel);
      }
    );
  };

  const openDetails = (details) => {
    const panel = getPanel(details);

    if (!panel) {
      details.open = true;
      return Promise.resolve();
    }

    const wasAnimating = details.classList.contains('is-wubs-closing') || details.classList.contains('is-wubs-opening');
    const current = wasAnimating
      ? captureCurrentState(details, panel)
      : { height: 0, paddingTop: '0px', paddingBottom: '0px' };

    if (details.open && !wasAnimating) {
      cleanupPanel(details, panel);
      return Promise.resolve();
    }

    details.open = true;
    const expanded = measureExpandedState(panel);

    if (prefersReducedMotion()) {
      cleanupPanel(details, panel);
      return Promise.resolve();
    }

    details.classList.remove('is-wubs-closing');
    details.classList.add('is-wubs-opening');
    panel.style.boxSizing = 'border-box';
    panel.style.height = `${current.height}px`;
    panel.style.paddingTop = current.paddingTop;
    panel.style.paddingBottom = current.paddingBottom;
    panel.style.opacity = current.height > 1 ? '1' : '0';
    panel.style.overflow = 'hidden';
    panel.style.transform = current.height > 1 ? 'translateY(0)' : 'translateY(-6px)';
    panel.getBoundingClientRect();

    return animatePanel(
      details,
      panel,
      {
        height: `${expanded.height}px`,
        paddingTop: expanded.paddingTop,
        paddingBottom: expanded.paddingBottom,
        opacity: '1',
        transform: 'translateY(0)'
      },
      { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      () => cleanupPanel(details, panel)
    );
  };

  const initGroup = (group) => {
    if (!group || group.dataset.wubsAccordionGroup === '1') return;
    group.dataset.wubsAccordionGroup = '1';

    const items = Array.from(group.querySelectorAll(':scope > details'));
    if (!items.length) return;

    let hasOpenItem = false;

    items.forEach((details) => {
      details.classList.add('is-wubs-accordion');

      const panel = getPanel(details);
      if (panel) panel.classList.add('is-wubs-accordion-panel');

      if (details.open) {
        if (hasOpenItem) {
          closeDetails(details, true);
        } else {
          hasOpenItem = true;
        }
      }

      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        const shouldOpen = !details.open || details.classList.contains('is-wubs-closing');
        const shouldStabilizeScroll = shouldOpen && group.matches('.wubs-agency-about__departments');

        if (!shouldOpen) {
          closeDetails(details);
          return;
        }

        const transitions = items
          .filter((item) => item !== details)
          .map((item) => closeDetails(item));

        transitions.push(openDetails(details));

        const transition = Promise.all(transitions);
        if (shouldStabilizeScroll) keepSummaryStable(summary, transition);
      });
    });
  };

  const init = (root = document) => {
    ensureStyle();

    GROUP_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach(initGroup);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
